import { defineStore } from "pinia";
import {
  SECTION_LIBRARY_PAGE_SIZE,
  SECTION_LIBRARY_SECTIONS_URL,
  SECTION_LIBRARY_TREE_URL,
  SECTION_LIBRARY_TYPES_URL,
} from "@studio/Constants/section-library-constants";

function galleryKey(sectionType, visualStyle, density) {
  return `${sectionType}|${visualStyle}|${density}`;
}

function failureMessage(what, error) {
  const detail = error?.response?.data?.message || error?.message || "no detail from the server";
  return `Section library: ${what} failed — ${detail}`;
}

// The section library's catalogue for the Library pages: section types with their template
// counts, and each type's approved templates (best first, sorted by the server).
export const useStudioSectionLibraryStore = defineStore("studio-section-library-store", {
  state: () => ({
    sectionLibraryTypes: [],
    isLoadingSectionLibraryTypes: false,
    sectionLibraryTypesError: "",
    librarySectionsByType: {},
    loadingLibrarySectionTypes: {},
    librarySectionsErrors: {},
    librarySectionTrees: {},
    libraryGalleryPages: {},
    loadingLibraryGalleryKeys: {},
  }),
  getters: {
    librarySectionTypeLabel: state => sectionType => {
      return state.sectionLibraryTypes.find(type => type.sectionType === sectionType)?.label ?? sectionType;
    },
    librarySectionsOfType: state => sectionType => state.librarySectionsByType[sectionType] ?? [],
    libraryGalleryPage: state => (sectionType, visualStyle, density) => {
      return state.libraryGalleryPages[galleryKey(sectionType, visualStyle, density)] ?? null;
    },
    isLoadingLibraryGalleryPage: state => (sectionType, visualStyle, density) => {
      return !!state.loadingLibraryGalleryKeys[galleryKey(sectionType, visualStyle, density)];
    },
    librarySectionByKey: state => (sectionType, variantKey) => {
      return (state.librarySectionsByType[sectionType] ?? []).find(section => section.variantKey === variantKey) ?? null;
    },
  },
  actions: {
    fetchSectionLibraryTypes() {
      if (this.isLoadingSectionLibraryTypes) {
        return Promise.resolve(this.sectionLibraryTypes);
      }
      this.isLoadingSectionLibraryTypes = true;
      return new Promise((resolve, reject) => {
        this.apiGet(
          SECTION_LIBRARY_TYPES_URL,
          {},
          data => {
            this.sectionLibraryTypes = data.types ?? [];
            this.sectionLibraryTypesError = "";
            resolve(this.sectionLibraryTypes);
          },
          error => {
            this.sectionLibraryTypesError = failureMessage("loading the section types", error);
            // eslint-disable-next-line no-console
            console.error(this.sectionLibraryTypesError);
            reject(new Error(this.sectionLibraryTypesError));
          },
          () => {
            this.isLoadingSectionLibraryTypes = false;
          },
        );
      });
    },
    // A template's element tree, ready to drop into a page; kept so a second drop is instant.
    fetchLibrarySectionTree(variantKey) {
      if (this.librarySectionTrees[variantKey]) {
        return Promise.resolve(this.librarySectionTrees[variantKey]);
      }
      return new Promise((resolve, reject) => {
        this.apiGet(
          SECTION_LIBRARY_TREE_URL(variantKey),
          {},
          data => {
            this.librarySectionTrees = { ...this.librarySectionTrees, [variantKey]: data };
            resolve(data);
          },
          error => reject(new Error(failureMessage(`loading the tree of ${variantKey}`, error))),
        );
      });
    },
    // One type's templates in one style and spacing, filtered by the server, for the Library gallery.
    fetchLibraryGalleryPage(sectionType, visualStyle, density) {
      const key = galleryKey(sectionType, visualStyle, density);
      if (this.libraryGalleryPages[key] || this.loadingLibraryGalleryKeys[key]) {
        return Promise.resolve(this.libraryGalleryPages[key] ?? []);
      }
      this.loadingLibraryGalleryKeys = { ...this.loadingLibraryGalleryKeys, [key]: true };
      return new Promise((resolve, reject) => {
        this.apiGet(
          SECTION_LIBRARY_SECTIONS_URL,
          { section_type: sectionType, tier: "", density, visual_style: visualStyle, limit: SECTION_LIBRARY_PAGE_SIZE, offset: 0 },
          data => {
            this.libraryGalleryPages = { ...this.libraryGalleryPages, [key]: data.sections ?? [] };
            this.librarySectionsErrors = { ...this.librarySectionsErrors, [sectionType]: "" };
            resolve(this.libraryGalleryPages[key]);
          },
          error => {
            const message = failureMessage(`loading the ${sectionType} templates`, error);
            this.librarySectionsErrors = { ...this.librarySectionsErrors, [sectionType]: message };
            // eslint-disable-next-line no-console
            console.error(message);
            reject(new Error(message));
          },
          () => {
            this.loadingLibraryGalleryKeys = { ...this.loadingLibraryGalleryKeys, [key]: false };
          },
        );
      });
    },
    fetchLibrarySectionsOfType(sectionType) {
      if (this.loadingLibrarySectionTypes[sectionType]) {
        return Promise.resolve(this.librarySectionsOfType(sectionType));
      }
      this.loadingLibrarySectionTypes = { ...this.loadingLibrarySectionTypes, [sectionType]: true };
      return new Promise((resolve, reject) => {
        this.apiGet(
          SECTION_LIBRARY_SECTIONS_URL,
          { section_type: sectionType, tier: "", density: "", visual_style: "", limit: SECTION_LIBRARY_PAGE_SIZE, offset: 0 },
          data => {
            this.librarySectionsByType = { ...this.librarySectionsByType, [sectionType]: data.sections ?? [] };
            this.librarySectionsErrors = { ...this.librarySectionsErrors, [sectionType]: "" };
            resolve(this.librarySectionsByType[sectionType]);
          },
          error => {
            const message = failureMessage(`loading the ${sectionType} templates`, error);
            this.librarySectionsErrors = { ...this.librarySectionsErrors, [sectionType]: message };
            // eslint-disable-next-line no-console
            console.error(message);
            reject(new Error(message));
          },
          () => {
            this.loadingLibrarySectionTypes = { ...this.loadingLibrarySectionTypes, [sectionType]: false };
          },
        );
      });
    },
  },
});
